import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';
import { FileUp, Wand2, Calendar } from 'lucide-react';
import { toast } from '../lib/toast';
import RichTextEditor from './RichTextEditor';
import TeamMemberSelect from './TeamMemberSelect';
import { generateTaskDescription } from '../lib/openai';

interface TaskFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  initialData?: {
    id: string;
    title: string;
    description: string;
    status: string;
    priority: string;
    agency_id: string;
    project_id: string;
    due_date: string;
    attachment_url: string | null;
    attachment_name: string | null;
  };
}

type Agency = Database['public']['Tables']['agencies']['Row'];
type Project = Database['public']['Tables']['projects']['Row'];

interface TeamMember {
  id: string;
  name: string;
  email: string;
  job_title: string;
  photo_url: string | null;
}

export default function TaskForm({ onSuccess, onCancel, initialData }: TaskFormProps) {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('Not Started');
  const [priority, setPriority] = useState('Normal');
  const [agencyId, setAgencyId] = useState(initialData?.agency_id || '');
  const [projectId, setProjectId] = useState(initialData?.project_id || '');
  const [dueDate, setDueDate] = useState(initialData?.due_date?.split('T')[0] || '');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const researchData = localStorage.getItem('taskFromResearch');

    try {
      if (researchData) {
        const data = JSON.parse(researchData);
        setTitle(data.title || initialData?.title || '');
        setDescription(data.description || initialData?.description || '');
        setStatus(data.status || initialData?.status || 'Not Started');
        setPriority(data.priority || initialData?.priority || 'Normal');

        const editor = document.querySelector('.ProseMirror');
        if (editor && data.description) {
          editor.innerHTML = `
            <h2>Research Results</h2>
            <div class="research-content">
              ${data.description}
            </div>
          `;
          setDescription(editor.innerHTML);
        }
        
        localStorage.removeItem('taskFromResearch');
      } else {
        setTitle(initialData?.title || '');
        setDescription(initialData?.description || '');
        setStatus(initialData?.status || 'Not Started');
        setPriority(initialData?.priority || 'Normal');
        
        const editor = document.querySelector('.ProseMirror');
        if (editor && initialData?.description) {
          editor.innerHTML = initialData.description;
        }
      }
    } catch (error) {
      console.error('Error loading research data:', error);
      toast.error('Failed to load research data');
    }
  }, [initialData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingData(true);
        setError(null);

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        let retries = 0;
        const maxRetries = 3;
        const delay = 1000;

        while (retries < maxRetries) {
          try {
            // Fetch all data in parallel
            const [agenciesResult, projectsResult, teamMembersResult] = await Promise.all([
              supabase
                .from('agencies')
                .select('id, name, contact_first_name, contact_last_name, email, phone, created_at, logo_url, additional_info')
                .eq('user_id', user.id)
                .order('name'),
              supabase
                .from('projects')
                .select('id, name, agency_name, priority, status, description, created_at')
                .eq('user_id', user.id)
                .order('name'),
              supabase
                .from('team_members')
                .select('id, name, email, job_title, photo_url, availability, admin_id, created_at, updated_at')
                .eq('admin_id', user.id)
                .order('name')
            ]);

            // Check for errors
            if (agenciesResult.error) throw agenciesResult.error;
            if (projectsResult.error) throw projectsResult.error;
            if (teamMembersResult.error) throw teamMembersResult.error;

            setAgencies(agenciesResult.data || []);
            setProjects(projectsResult.data || []);
            setTeamMembers(teamMembersResult.data || []);

            // If editing, fetch existing assignments
            if (initialData?.id) {
              const { data: assignments, error: assignmentsError } = await supabase
                .from('task_assignments')
                .select('team_member_id')
                .eq('task_id', initialData.id);
              
              if (assignmentsError) throw assignmentsError;
              setSelectedMembers(assignments?.map(a => a.team_member_id) || []);
            }

            break;
          } catch (error) {
            retries++;
            if (retries === maxRetries) throw error;
            await new Promise(resolve => setTimeout(resolve, delay * retries));
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load form data. Please try again.');
        toast.error('Failed to load form data');
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [initialData?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let attachment_url = initialData?.attachment_url || null;
      let attachment_name = initialData?.attachment_name || null;

      if (file) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;

        const { error: uploadError, data } = await supabase.storage
          .from('task-attachments')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        if (data) {
          const { data: { publicUrl } } = supabase.storage
            .from('task-attachments')
            .getPublicUrl(fileName);
          
          attachment_url = publicUrl;
          attachment_name = file.name;
        }
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const taskData = {
        title,
        description,
        status,
        priority,
        agency_id: agencyId || null,
        project_id: projectId || null,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
        attachment_url,
        attachment_name,
        user_id: user.id,
      };

      if (initialData) {
        const { error } = await supabase
          .from('tasks')
          .update(taskData)
          .eq('id', initialData.id);
        if (error) throw error;

        // Update assignments
        await supabase
          .from('task_assignments')
          .delete()
          .eq('task_id', initialData.id);

        if (selectedMembers.length > 0) {
          const assignments = selectedMembers.map(memberId => ({
            task_id: initialData.id,
            team_member_id: memberId,
            user_id: user.id // Add user_id to each assignment
          }));
          
          const { error: assignError } = await supabase
            .from('task_assignments')
            .insert(assignments);
          if (assignError) throw assignError;
        }

        toast.success('Task updated successfully');
      } else {
        const { error, data } = await supabase
          .from('tasks')
          .insert([taskData])
          .select()
          .single();
        if (error) throw error;

        // Create assignments for new task
        if (selectedMembers.length > 0 && data) {
          const assignments = selectedMembers.map(memberId => ({
            task_id: data.id,
            team_member_id: memberId,
            user_id: user.id // Add user_id to each assignment
          }));
          
          const { error: assignError } = await supabase
            .from('task_assignments')
            .insert(assignments);
          if (assignError) throw assignError;
        }

        toast.success('Task created successfully');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving task:', error);
      setError((error as Error).message);
      toast.error('Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (!title) {
      setError('Please enter a task title first');
      toast.error('Please enter a task title to generate a description');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    
    try {
      const generatedDescription = await generateTaskDescription(title);
      // Set the description state directly
      setDescription(generatedDescription);
      toast.success('Task description generated successfully');
    } catch (error) {
      setError((error as Error).message);
      toast.error('Failed to generate description');
    } finally {
      setIsGenerating(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="title" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Title
        </label>
        <input
          type="text"
          id="title"
          required
          className="block w-full rounded-lg shadow-sm sm:text-sm dark:text-white"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Description
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleGenerateDescription}
              disabled={isGenerating}
              className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/50 dark:text-indigo-300 dark:hover:bg-indigo-900/70"
            >
              <Wand2 className="w-3 h-3 mr-1" />
              {isGenerating ? 'Generating...' : 'Generate with AI'}
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
              Add OpenAI API key in Settings to use AI features
            </span>
          </div>
        </label>
        <RichTextEditor
          content={description}
          onChange={setDescription}
        />
      </div>

      <div>
        <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Status
        </label>
        <select
          id="status"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="Not Started">Not Started</option>
          <option value="In Progress">In Progress</option>
          <option value="Testing">Testing</option>
          <option value="Awaiting Feedback">Awaiting Feedback</option>
          <option value="Completed">Completed</option>
        </select>
      </div>

      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
        <div>
          <label htmlFor="customer" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Customer
          </label>
          <select
            id="customer"
            className="block w-full rounded-lg shadow-sm sm:text-sm dark:text-white"
            value={agencyId}
            onChange={(e) => setAgencyId(e.target.value)}
          >
            <option value="">Select Customer</option>
            {agencies.map((agency) => (
              <option key={agency.id} value={agency.id}>
                {agency.name}
              </option>
            ))}
          </select>
          {agencies.length === 0 && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No customers found. Please add a customer first.
            </p>
          )}
        </div>

        <div>
          <label htmlFor="project" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Project
          </label>
          <select
            id="project"
            className="block w-full rounded-lg shadow-sm sm:text-sm dark:text-white"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">Select Project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          {projects.length === 0 && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No projects found. Please add a project first.
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="priority" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Priority
        </label>
        <select
          id="priority"
          className="block w-full rounded-lg shadow-sm sm:text-sm dark:text-white"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
        >
          <option value="Low">Low</option>
          <option value="Normal">Normal</option>
          <option value="High">High</option>
          <option value="Urgent">Urgent</option>
        </select>
      </div>

      <div>
        <label htmlFor="due-date" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Due Date
        </label>
        <div className="relative mt-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Calendar className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="date"
            id="due-date"
            className="block w-full pl-10 rounded-lg shadow-sm sm:text-sm dark:text-white appearance-none"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            style={{ paddingLeft: '2.5rem' }}
          />
        </div>
      </div>

      <div>
        <label htmlFor="team-members" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Assign Team Members
        </label>
        <TeamMemberSelect
          teamMembers={teamMembers}
          selectedTeamMembers={selectedMembers}
          onChange={setSelectedMembers}
        />
        {teamMembers.length === 0 && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            No team members found. Please add team members first.
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
          Attachment
        </label>
        <div className="flex items-center space-x-4">
          <label
            htmlFor="file-upload"
            className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
          >
            <FileUp className="h-5 w-5 inline-block mr-2" />
            Choose file
          </label>
          <input
            id="file-upload"
            type="file"
            className="sr-only"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {(file || initialData?.attachment_name) && (
            <span className="ml-3 text-sm text-gray-500">
              <span className="font-medium">{file?.name || initialData?.attachment_name}</span>
              <button
                type="button"
                className="ml-2 text-indigo-600 hover:text-indigo-500"
                onClick={() => setFile(null)}
              >
                Remove
              </button>
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/50 p-4 border border-red-200 dark:border-red-800">
          <div className="text-sm text-red-700 dark:text-red-200">{error}</div>
        </div>
      )}

      <div className="flex justify-end space-x-4 pt-4">
        <button
          type="button"
          className="inline-flex justify-center py-2.5 px-5 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:bg-gray-600"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2.5 px-5 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : initialData ? 'Update Task' : 'Create Task'}
        </button>
      </div>
    </form>
  );
}